import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { Alert, Box, Button, Skeleton, Typography } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { Select } from 'components/common';
import type { AdSettingsRequestAdSet } from '../api';
import useAdSettings from '../api/getAdSettings';
import adSettingsMapper from '../utils/adSettings.mapper';
import { pickDefaultAdSettingItem } from '../utils/adSettingsPages';
import { mergeAdSettingsSlot } from '../validation/adSettingsValidation';

interface AdSettingsFieldsSectionProps {
  settingsIndex: number;
  fieldName: string;
  app: string;
  platform: string;
  adSets: AdSettingsRequestAdSet[];
  isPartnershipAds: boolean;
  partnershipId?: string;
  appPageId?: string;
  sectionTitle?: string;
}

const AdSettingsFieldsSection: FC<AdSettingsFieldsSectionProps> = ({
  settingsIndex,
  fieldName,
  app,
  platform,
  adSets,
  isPartnershipAds,
  partnershipId,
  appPageId = '',
  sectionTitle,
}) => {
  const { setValue, trigger, getValues } = useFormContext();
  const normalizedAppPageId = (appPageId ?? '').trim();
  const isPartnershipSelected = Boolean(partnershipId?.trim());
  const canFetchPartnershipAdSettings =
    !isPartnershipAds || (isPartnershipSelected && Boolean(normalizedAppPageId));
  const partnershipKey = `${(partnershipId ?? '').trim()}:${normalizedAppPageId}`;
  const previousPartnershipKeyRef = useRef<string | null>(null);

  const {
    data: adSettings,
    isError,
    error,
    isSuccess: isSuccessAdSettings,
    isLoading: isLoadingAdSettings,
    refetch: refetchAdSettings,
  } = useAdSettings({
    params: { app, platform, ad_sets: adSets },
    isPartnershipAds,
    appPageId: normalizedAppPageId,
    config: {
      select: adSettingsMapper,
      enabled: canFetchPartnershipAdSettings,
    },
  });

  useEffect(() => {
    if (!canFetchPartnershipAdSettings) {
      return;
    }

    if (!isSuccessAdSettings || !adSettings) {
      return;
    }

    const selectedSettings = adSettings.reduce<
      Record<string, { page_id: number | ''; text_id: number | '' }>
    >(
      (acc, { name, pages, texts }) => {
        const defaultPage = pickDefaultAdSettingItem(pages);
        const defaultText = pickDefaultAdSettingItem(texts);

        return {
          ...acc,
          [name]: {
            page_id: defaultPage?.id ?? '',
            text_id: defaultText?.id ?? '',
          },
        };
      },
      {},
    );

    if (isEmpty(selectedSettings)) {
      return;
    }

    const previousPartnershipKey = previousPartnershipKeyRef.current;
    // Preserve deep-linked page/text on first load; after the user changes
    // partnership, apply that partnership's template instead of keeping the old ids.
    const shouldReplaceWithPartnershipTemplate =
      isPartnershipAds &&
      previousPartnershipKey != null &&
      previousPartnershipKey !== partnershipKey;

    previousPartnershipKeyRef.current = partnershipKey;

    const existing = shouldReplaceWithPartnershipTemplate
      ? {}
      : (getValues(`${fieldName}.${settingsIndex}`) as Record<string, unknown> | undefined) ?? {};

    setValue(
      `${fieldName}.${settingsIndex}`,
      mergeAdSettingsSlot(selectedSettings, existing),
      { shouldValidate: false },
    );
    void trigger(fieldName);
  }, [
    adSettings,
    canFetchPartnershipAdSettings,
    fieldName,
    getValues,
    isPartnershipAds,
    isSuccessAdSettings,
    partnershipKey,
    setValue,
    settingsIndex,
    trigger,
  ]);

  if (isPartnershipAds && !canFetchPartnershipAdSettings) {
    return (
      <Alert severity="info" variant="standard" sx={{ width: 'fit-content' }}>
        Ad settings are not loaded because partnership page is not selected.
      </Alert>
    )
  }

  if (isLoadingAdSettings) {
    return (
        <Skeleton
          height={40}
          width={'50%'}
          sx={{ transform: 'translateY(0)', borderRadius: '8px' }}
        />
    );
  }

  if (isError) {
    return (
      <Alert
        severity="error"
        variant="standard"
        sx={{ width: 'fit-content' }}
        action={(
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              void refetchAdSettings();
            }}
          >
            Retry
          </Button>
        )}
      >
        {(error as { cause?: { error?: string } })?.cause?.error ??
          'Error occurred. Please try again.'
        }
      </Alert>
    );
  }

  if ((!adSettings || isEmpty(adSettings)) && !isError && canFetchPartnershipAdSettings) {
    return (
      <Alert severity="error" variant="standard" sx={{ width: 'fit-content' }}>
        Ad settings not found for selected ad sets.
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {sectionTitle && (
        <Typography variant="subtitle2" fontWeight={500}>
          {sectionTitle}
        </Typography>
      )}
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        data-testid={`adSettingsForm-${settingsIndex}`}
      >
        {adSettings?.map((setting, i) => (
          <Box
            key={`${setting.name}.${i}`}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <Typography variant={'body2'}>{setting.name}</Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                gap: 2,
              }}
            >
              <Box sx={{ width: '100%', display: isPartnershipAds ? 'none' : 'block' }}>
                <Select
                  disabled={!setting?.pages?.length}
                  name={`${fieldName}.${settingsIndex}.${setting.name}.page_id`}
                  sx={{ '.MuiInputBase-root': { backgroundColor: 'white' } }}
                  options={
                    setting?.pages?.map((s) => ({
                      value: s.id,
                      label: s.name,
                    })) ?? []
                  }
                  inputLabel={'Page'}
                />
              </Box>
              <Box sx={{ width: '100%' }}>
                <Select
                  disabled={!setting?.texts?.length}
                  sx={{ '.MuiInputBase-select': { whiteSpace: 'wrap' }, '.MuiInputBase-root': { backgroundColor: 'white' } }}
                  name={`${fieldName}.${settingsIndex}.${setting.name}.text_id`}
                  options={
                    setting?.texts?.map((s) => ({
                      value: s.id,
                      label: s.name,
                    })) ?? []
                  }
                  inputLabel={'Text'}
                />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default AdSettingsFieldsSection;
